import { FC, useEffect, useState } from 'react';
import {
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogContent,
  Button,
  DialogActions,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  IconButton,
  Grid,
} from '@mui/material';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import Settings2FillIcon from 'remixicon-react/Settings2FillIcon';
import UserSettingsFillIcon from 'remixicon-react/UserSettingsFillIcon';
import ListSettingsFillIcon from 'remixicon-react/ListSettingsFillIcon';
import BracketsLine from 'remixicon-react/BracesFillIcon';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import CompanyAPI from 'api/CompanyAPI';
import { CompanyType, updateCompaniesQueryCache } from 'hooks/useCompanyQueries';
import { catchErrorMessage, hasJsonChanged } from 'utility/helpers';
import JSONEditorWrapper, { JSONType } from '@buildwise/libs/common/JSONEditor/JSONEditorWrapper';
import GrowDialogTransition from 'components/modal/GrowDialogTransition';
import { ReactComponent as ExpandDiagonalLineIcon } from 'assets/icons/ExpandDiagonalLineIcon.svg';
import { ReactComponent as CollapseDiagonalLineIcon } from 'assets/icons/CollapseDiagonalLineIcon.svg';

type UpdateCompanySettingsFormValuesType = {
  isActive: string;
  externalFlag: string;
  sendNotificationFlag: boolean;
  emailWeeklySummaryFlag: boolean;
  pmSyncFlag: boolean;
  companyJson: JSONType;
};

type ModifyCompanyModalProps = {
  company: CompanyType;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onCloseCallback?: () => void;
};

const ModifyCompanyModal: FC<ModifyCompanyModalProps> = ({
  company,
  open,
  setOpen,
  onCloseCallback,
}) => {
  const [isFullScreen, setFullScreen] = useState(false);
  const [json, setJson] = useState<JSONType>(company);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('general');
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty, isValid },
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      isActive: company.isActive ? 'Active' : 'Inactive',
      externalFlag: company.settings?.externalFlag ? 'External' : 'Internal',
      sendNotificationFlag: company.settings?.sendNotificationFlag || false,
      emailWeeklySummaryFlag: company.settings?.emailWeeklySummaryFlag || false,
      pmSyncFlag: company.settings?.pmSyncFlag || false,
      companyJson: company as JSONType,
    },
  });

  useEffect(() => {
    setValue('companyJson', json, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [json]);

  const closeModal = () => {
    reset();
    setOpen(false);
    onCloseCallback?.();
  };

  const onSubmit = async (values: UpdateCompanySettingsFormValuesType) => {
    try {
      setLoading(true);
      let payload = hasJsonChanged(company, values?.companyJson)
        ? values?.companyJson
        : {
            id: company.id,
            isActive: values.isActive === 'Active',
            settings: {
              ...company.settings,
              externalFlag: values.externalFlag === 'External',
              sendNotificationFlag: values.sendNotificationFlag,
              emailWeeklySummaryFlag: values.emailWeeklySummaryFlag,
              pmSyncFlag: values.pmSyncFlag,
            },
          };
      const { data } = await CompanyAPI.updateCompanySettings(company.id, payload);
      updateCompaniesQueryCache(data, 'update');
      toast.success('Company settings updated');
      closeModal();
    } catch (error) {
      console.error('UPDATE_COMPANY_ERROR', error);
      toast.error(catchErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = handleSubmit(onSubmit);

  return (
    <Dialog
      open={open}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      maxWidth="lg"
      fullWidth
      fullScreen={isFullScreen}
      TransitionComponent={GrowDialogTransition}
      sx={{ '& .MuiPaper-root': { height: '100%' } }}
    >
      <CustomDialogTitle onClose={closeModal} id="dialog-title">
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <$Title>Modify Company</$Title>
          <IconButton
            onClick={() => setFullScreen((prevState) => !prevState)}
            sx={{ color: 'custom.black', mr: 4 }}
          >
            {isFullScreen ? (
              <CollapseDiagonalLineIcon width={24} />
            ) : (
              <ExpandDiagonalLineIcon width={24} />
            )}
          </IconButton>
        </Box>
      </CustomDialogTitle>
      <DialogContent
        sx={{
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
        dividers
      >
        <Typography mb={2}>
          Company Name : <strong>{company.name}</strong>
        </Typography>
        <Tabs value={tab} onChange={(_: unknown, newValue: string) => setTab(newValue)}>
          <Tab
            icon={<Settings2FillIcon size={20} />}
            iconPosition="start"
            key="general"
            value="general"
            label="General"
          />
          <Tab
            icon={<UserSettingsFillIcon size={20} />}
            iconPosition="start"
            key="notifications"
            value="notifications"
            label="Notifications"
          />
          <Tab
            icon={<ListSettingsFillIcon size={20} />}
            iconPosition="start"
            key="integrations"
            value="integrations"
            label="Integrations"
          />
          <Tab
            icon={<BracketsLine size={20} />}
            iconPosition="start"
            key="jsonEditor"
            value="jsonEditor"
            label="JSON Editor"
          />
        </Tabs>

        {tab === 'general' && (
          <Grid mt={1} container sm={4} lg={4}>
            <$SettingRow>
              <Typography>Subscription Status</Typography>
              <Controller
                control={control}
                render={({ field }) => (
                  <RadioGroup row {...field}>
                    <FormControlLabel
                      control={<Radio size="small" />}
                      value="Active"
                      label="Active"
                      disabled={loading}
                    />
                    <FormControlLabel
                      control={<Radio size="small" />}
                      value="Inactive"
                      label="Inactive"
                      disabled={loading}
                    />
                  </RadioGroup>
                )}
                name="isActive"
              />
            </$SettingRow>
            <$SettingRow>
              <Typography>Company Type</Typography>
              <Controller
                control={control}
                render={({ field }) => (
                  <RadioGroup row {...field}>
                    <FormControlLabel
                      control={<Radio size="small" />}
                      value="External"
                      label="External"
                      disabled={loading}
                    />
                    <FormControlLabel
                      control={<Radio size="small" />}
                      value="Internal"
                      label="Internal"
                      disabled={loading}
                    />
                  </RadioGroup>
                )}
                name="externalFlag"
              />
            </$SettingRow>
          </Grid>
        )}

        {tab === 'notifications' && (
          <Grid mt={1} container sm={4} lg={4}>
            <$SettingRow>
              <Typography>Enable Notifications</Typography>
              <Controller
                control={control}
                render={({ field }) => (
                  <Switch disableRipple {...field} disabled={loading} checked={field.value} />
                )}
                name="sendNotificationFlag"
              />
            </$SettingRow>
            <$SettingRow>
              <Typography>Send Weekly Activity Summary Emails To Users</Typography>
              <Controller
                control={control}
                render={({ field }) => (
                  <Switch disableRipple {...field} disabled={loading} checked={field.value} />
                )}
                name="emailWeeklySummaryFlag"
              />
            </$SettingRow>
          </Grid>
        )}

        {tab === 'integrations' && (
          <Grid mt={1} container sm={4} lg={4}>
            <$SettingRow>
              <Typography>Sync Project Data From PM Systems</Typography>
              <Controller
                control={control}
                render={({ field }) => (
                  <Switch disableRipple {...field} disabled={loading} checked={field.value} />
                )}
                name="pmSyncFlag"
              />
            </$SettingRow>
          </Grid>
        )}

        {tab === 'jsonEditor' && (
          <Box mt={1} height={'100%'}>
            <JSONEditorWrapper initialJson={json} setJson={setJson} defaultMode="code" />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          disabled={loading || !(isDirty && isValid)}
          onClick={handleSave}
        >
          {loading ? <CircularProgress sx={{ color: '#000' }} size={20} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const $Title = styled(Typography, { label: 'DialogTitle' })({
  fontSize: '24px',
  fontWeight: 700,
  color: 'black',
});

const $SettingRow = styled(Box, { label: 'SettingRow' })({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '4px 0',
});

export default ModifyCompanyModal;
